import React, { useState } from 'react';
import ColorPicker from 'react-best-gradient-color-picker';
import { Palette } from 'lucide-react';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import { rgbToHex } from '../../../utils/helper';
import InlineTooltip from '../Tooltip/InlineTooltip';

const GradientColorPicker = ({ value, onChange, label, id, error, mandatory, helpText }) => {
    const [showPicker, setShowPicker] = useState(false);
    const [selectedColor, setSelectedColor] = useState(value);
    const { content } = useContentfulContext();

    function convertGradientRgbToHex(str) {
        // Replace all rgb/rgba/RGB/RGBA color stops with their hex equivalents (case insensitive)
        return str.replace(/rgba?\([^)]+\)/gi, match => rgbToHex(match));
    }

    const handleConfirmColor = () => {
        let hexColor = selectedColor;
        if (typeof selectedColor === 'string' && selectedColor.includes('rgb')) {
          hexColor = convertGradientRgbToHex(selectedColor);
        }
        onChange(hexColor);
        setShowPicker(false);
    };

    return (
        <div className="mb-4">
            {/* Input Field */}
            <div className="relative" dzt-field-id={id}>
                <input
                    type="text"
                    name={id}
                    className="block px-2.5 pb-2.5 pt-4 w-full pr-10 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary-2 peer"
                    placeholder=" "
                    value={value}
                    readOnly
                />
                <label
                    htmlFor={id}
                    className="absolute text-sm text-black duration-300 transform
                            -translate-y-4 scale-75 top-2 z-10 origin-[0]
                            bg-gray-50 px-2 left-1 flex items-center gap-1
                            peer-focus:px-2 peer-focus:text-primary
                            peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2
                            peer-placeholder-shown:top-1/2
                            peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4"
                >
                    <span>
                        {label} {mandatory && '*'}
                    </span>

                    {helpText && (
                        <InlineTooltip
                            content={helpText}
                            imgClass="pt-0.5"
                        />
                    )}
                </label>
                {/* Palette Icon */}
                <Palette
                    className="absolute right-4 sm:right-6 top-1/2 transform -translate-y-1/2 h-[18px] w-[18px] cursor-pointer"
                    onClick={() => setShowPicker(true)}
                />
            </div>
            {error && <p className="mt-2 text-sm text-red-5">{error}</p>}

            {/* Modal for Color Picker */}
            {showPicker && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-50">
                    <div className="w-full max-w-xl flex flex-col items-center rounded-lg bg-white px-10 py-6 shadow-lg">
                        <h2 className="mb-4 text-lg font-semibold self-start">{content.INPUT_COLOR_PICKER_CONTENT.title}</h2>
                        <div
                            className="max-h-[550px] overflow-hidden scrollbar-width-2px flex items-center"
                        >
                            <ColorPicker
                                value={selectedColor}
                                onChange={setSelectedColor}
                                hidePresets={true}
                                width={510}
                                showHexAlpha={true}
                            />
                        </div>
                        <div className="mt-4 flex justify-end gap-2 self-end">
                            <button
                                className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                onClick={() => setShowPicker(false)}
                            >
                                {content.INPUT_COLOR_PICKER_CONTENT.cancel_btn}
                            </button>
                            <button
                                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary-2 focus:ring-offset-2"
                                onClick={handleConfirmColor}
                            >
                                {content.INPUT_COLOR_PICKER_CONTENT.pick_btn}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default GradientColorPicker